import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useMissingFields } from "@/components/app/fields/use-missing-fields";
import { Plus } from "@/lib/icons";
import { toast } from "sonner";
import { VITAL_METRICS, formatTimestampInTimezone, errorWords } from "@alm/domain";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { CurrentVitals } from "@/components/app/vitals/current-vitals";
import { RecordVitalsDialog } from "@/components/app/vitals/record-vitals-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { readState } from "@/lib/query/read-state";
import { useZone } from "@/lib/facility/zone";
import { ProfileSection } from "./-components/chart/profile-section";
import { SectionEmpty } from "@/routes/_authenticated/_care/residents/$residentId/-components/chart/section-empty";
import { VitalsHistory } from "./-components/chart/vitals-history";
import { type VitalsEntry } from "@/routes/_authenticated/_care/residents/$residentId/-components/chart/vitals-history-types";

const fail = (error: Error) => toast.error(errorWords(error));

export const Route = createFileRoute("/_authenticated/_care/residents/$residentId/vitals")({
  component: VitalsTab,
});

function VitalsTab() {
  const { residentId } = Route.useParams();
  const { data: resident } = useSuspenseQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId } }),
  );
  const canWrite = useCan("resident.clinical:update", resident.facilityId);
  const zone = useZone(resident.facilityId);
  const queryClient = useQueryClient();
  const [recording, setRecording] = useState(false);
  const [voiding, setVoiding] = useState<{ id: string; reason: string } | null>(null);
  const [editing, setEditing] = useState<VitalsEntry | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const fields = useMissingFields(
    {
      "void-reason":
        (voiding?.reason.trim().length ?? 0) < 3 ? "Give a reason of at least 3 characters." : null,
    },
    voiding?.id,
  );
  const latest = useQuery(
    orpc.care.residents.vitals.latest.queryOptions({ input: { residentId } }),
  );
  const feed = useQuery(
    orpc.care.residents.vitals.list.queryOptions({ input: { residentId, includeVoided: true } }),
  );
  const voidEntry = useMutation(
    orpc.care.residents.vitals.void.mutationOptions({
      onSuccess: async () => {
        toast.success("Entry voided");
        setVoiding(null);
        await queryClient.invalidateQueries({ queryKey: orpc.care.residents.vitals.key() });
      },
      onError: fail,
    }),
  );
  const count = VITAL_METRICS.filter((metric) => latest.data?.metrics[metric]).length;
  // A failed read gets its own state on both cards: "nothing recorded yet" is
  // a statement about the resident, and a failed read has not made one.
  const current = readState(latest, count);
  const history = readState(feed, feed.data?.length ?? 0);

  return (
    <div className="space-y-6">
      <ProfileSection
        title="Current vitals"
        description={`Most recent value of each measurement${latest.data?.asOf ? `, as of ${formatTimestampInTimezone(latest.data.asOf, zone)}` : ""}. These are the values the face sheet, Physician POC and care plan pull.`}
        action={
          canWrite ? (
            <Button size="sm" onClick={() => setRecording(true)}>
              <Plus className="size-4" />
              Record vitals
            </Button>
          ) : null
        }
      >
        {current === "loading" ? (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {["bp", "pulse", "temp", "spo2"].map((slot) => (
              <Skeleton key={slot} className="h-14 w-full" />
            ))}
          </div>
        ) : current === "failed" ? (
          <ReadFailed
            message="Could not load the current vitals. None are shown because none were read, not because none are on file."
            error={latest.error}
            retrying={latest.isFetching}
            onRetry={() => void latest.refetch()}
          />
        ) : current === "empty" || !latest.data ? (
          <SectionEmpty
            title="No vitals recorded yet"
            detail="Record a set here, or they will appear automatically as they are captured during a med pass."
          />
        ) : (
          <CurrentVitals latest={latest.data} zone={zone} />
        )}
      </ProfileSection>
      <ProfileSection
        title="History"
        description="Every recorded set, newest first, including vitals captured during a med pass."
      >
        {history === "loading" ? (
          <Skeleton className="h-24" />
        ) : history === "failed" ? (
          <ReadFailed
            message="Could not load the history. Nothing is listed because nothing was read, not because nothing was recorded."
            error={feed.error}
            retrying={feed.isFetching}
            onRetry={() => void feed.refetch()}
          />
        ) : history === "ready" && feed.data ? (
          <VitalsHistory
            entries={feed.data}
            canWrite={canWrite}
            onEdit={(entry) => {
              setEditing(entry);
              setEditOpen(true);
            }}
            onVoid={(entry) => setVoiding({ id: entry.id, reason: "" })}
          />
        ) : (
          <SectionEmpty title="Nothing recorded yet" detail="Recorded sets will appear here." />
        )}
      </ProfileSection>
      <RecordVitalsDialog
        residentId={residentId}
        open={recording}
        onOpenChange={setRecording}
        zone={zone}
      />
      <RecordVitalsDialog
        residentId={residentId}
        open={editOpen}
        onOpenChange={setEditOpen}
        editing={editing}
        zone={zone}
      />
      <AlertDialog open={voiding !== null} onOpenChange={(open) => !open && setVoiding(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Void this entry</AlertDialogTitle>
            <AlertDialogDescription>
              The row is kept for audit and leaves the feed and the current values.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="void-reason">Reason</Label>
            <Input
              id="void-reason"
              value={voiding?.reason ?? ""}
              onChange={(e) => setVoiding((v) => (v ? { ...v, reason: e.target.value } : v))}
              placeholder="Charted on the wrong resident"
              {...fields.invalid("void-reason")}
            />
            {fields.error("void-reason")}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={voidEntry.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (!voiding || !fields.check()) return;
                voidEntry.mutate({ vitalsId: voiding.id, reason: voiding.reason.trim() });
              }}
            >
              {voidEntry.isPending ? "Voiding…" : "Void entry"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
